/* eslint-disable react/prop-types */
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react'
import {
  CampaignReadyModal,
  DiscountModal,
  SinglePurchaseModal,
  ExitFeedbackModal,
  PaymentSuccessModal,
} from '../../../../components/CampaignUpsellModals'
import {
  UPSELL_FLOWS,
  useUpsellCatalog,
  useUpgradePlansTab,
  useUpsellEventMap,
  useUpsellFlowState,
  useUpsellCheckout,
  useUpsellSuccessClose,
  trackUpsellExitFeedback,
  resolvePlanCheckoutPayload,
  resolvePackageCheckoutPayload,
} from '../UpsellFlows'
import {
  UPSELL_FEATURED_PLAN_TIER,
  filterStaticPlanOptions,
  UPSELL_CAMPAIGN_PACKAGE_CODE,
  UPSELL_DOWNLOAD_PACKAGE_CODE,
} from '../UpsellFlows/upsellCatalog'
import {
  DISCOUNT_OFFER,
  DOWNLOAD_UPSELL_STEPS,
  DOWNLOAD_UPSELL_TABS,
  EXIT_FEEDBACK_OPTIONS,
  PLAN_FEATURED,
  PLAN_OPTIONS,
} from './constants'
import styles from './styles.module.css'

const BACK_TO_CAMPAIGN_ACTION = { label: 'Voltar para esta campanha' }
const CREATE_NEXT_CAMPAIGN_ACTION = { label: 'Criar minha próxima campanha' }

export const CampaignUpsellFlow = forwardRef(function CampaignUpsellFlow(
  {
    enabled = false,
    campaignId = null,
    showPreviewButton = false,
    onOpen = null,
  },
  ref,
) {
  const catalog = useUpsellCatalog({ enabled })
  const {
    openSubscriptionCheckout,
    openAddonCheckout,
  } = useUpsellCheckout()
  const {
    activeTab,  
    setActiveTab,
    closeFlow,
    openFlow,
    isStep,
    goToStep,
    openSuccessStep,
    currentStep,
    isSuccessStepActive,
  } = useUpsellFlowState({
    enabled,
    initialStep: DOWNLOAD_UPSELL_STEPS.READY,
  })
  const upsellTabs = useUpgradePlansTab(
    catalog,
    activeTab,
    setActiveTab,
    DOWNLOAD_UPSELL_TABS,
  )

  useUpsellEventMap({
    flow: UPSELL_FLOWS.CAMPAIGN_DOWNLOAD,
    campaignId,
    currentStep,
    activeTab,
  })

  const featuredPlanOffer = useMemo(() => {
    const base =
      catalog.buildFeaturedPlanOffer() ?? {
        price: 'R$ 149,90/mês',
        unitPrice: 'R$ 2,14 por arte',
        name: PLAN_FEATURED.name,
        artsPerMonth: PLAN_FEATURED.artsPerMonth,
      }

    const planName = base.name ?? PLAN_FEATURED.name
    const artsPerMonth = base.artsPerMonth ?? PLAN_FEATURED.artsPerMonth

    return {
      ...base,
      label: `Plano ${planName} · ${artsPerMonth} artes/mês + download liberado`,
      description: 'Para quem cria com frequência. Cancele quando quiser.',
    }
  }, [catalog])

  const featuredUpgradePlan = catalog.featuredUpgradePlan

  const featuredPackageOffer = useMemo(
    () =>
      catalog.buildFeaturedPackageOffer(catalog.campaignPackage, {
        description: 'Pacote de 10 artes extras + download desta campanha.',
        subdescription: 'use quando quiser · sem mensalidade',
      }),
    [catalog],
  )

  const packageOptions = useMemo(
    () => catalog.getPackageOptions([UPSELL_CAMPAIGN_PACKAGE_CODE]),
    [catalog],
  )

  const planOptions = useMemo(() => {
    const featuredTier =
      catalog.featuredUpgradePlan?.tier ?? UPSELL_FEATURED_PLAN_TIER

    if (!catalog.dbPlans.length) {
      return filterStaticPlanOptions(
        PLAN_OPTIONS,
        catalog.currentPlanTier,
        featuredTier,
      )
    }

    return catalog.getUpgradePlanOptions()
  }, [catalog])

  const handleOpenPackageCheckout = useCallback((option) => {
    openAddonCheckout({
      ...resolvePackageCheckoutPayload(
        option,
        catalog.campaignPackage,
        UPSELL_CAMPAIGN_PACKAGE_CODE,
      ),
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.CAMPAIGN_DOWNLOAD,
      upsellSuccessStep: DOWNLOAD_UPSELL_STEPS.SUCCESS,
      onOpen: closeFlow,
    })
  }, [campaignId, catalog.campaignPackage, closeFlow, openAddonCheckout])

  const handleOpenPlanCheckout = useCallback((option) => {
    openSubscriptionCheckout({
      ...resolvePlanCheckoutPayload(option, featuredUpgradePlan),
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.CAMPAIGN_DOWNLOAD,
      upsellSuccessStep: DOWNLOAD_UPSELL_STEPS.SUCCESS_PLAN,
      onOpen: closeFlow,
    })
  }, [campaignId, closeFlow, featuredUpgradePlan, openSubscriptionCheckout])

  const handleOpenFeaturedPackageCheckout = useCallback(() => {
    handleOpenPackageCheckout(catalog.campaignPackage)
  }, [catalog.campaignPackage, handleOpenPackageCheckout])

  const handleOpenDiscountPackageCheckout = useCallback(() => {
    openAddonCheckout({
      ...resolvePackageCheckoutPayload(
        null,
        catalog.campaignPackage,
        UPSELL_CAMPAIGN_PACKAGE_CODE,
      ),
      price: DISCOUNT_OFFER.price,
      promotionCode: DISCOUNT_OFFER.couponCode,
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.CAMPAIGN_DOWNLOAD,
      upsellSuccessStep: DOWNLOAD_UPSELL_STEPS.SUCCESS_DISCOUNT,
      onOpen: closeFlow,
    })
  }, [campaignId, catalog.campaignPackage, closeFlow, openAddonCheckout])

  const handleOpenSingleDownloadCheckout = useCallback(() => {
    if (!campaignId) return

    openAddonCheckout({
      packageCode: UPSELL_DOWNLOAD_PACKAGE_CODE,
      name: 'Download da campanha',
      price: 19,
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.CAMPAIGN_DOWNLOAD,
      upsellSuccessStep: DOWNLOAD_UPSELL_STEPS.SUCCESS_SINGLE,
      onOpen: closeFlow,
    })
  }, [campaignId, closeFlow, openAddonCheckout])

  const handleOpenFeaturedPlanCheckout = useCallback(() => {
    handleOpenPlanCheckout(featuredUpgradePlan)
  }, [featuredUpgradePlan, handleOpenPlanCheckout])

  const closeSuccessFlow = useUpsellSuccessClose(closeFlow, isSuccessStepActive)

  const withClose = (action) => ({
    ...action,
    onClick: closeSuccessFlow,
  })

  useImperativeHandle(ref, () => ({
    open: () => {
      const result = openFlow()
      if (result) {
        onOpen?.()
      }
      return result
    },
    close: closeFlow,
    openSuccessStep,
  }), [openFlow, closeFlow, openSuccessStep, onOpen])

  if (!enabled && !isSuccessStepActive) {
    return null
  }

  const packagesContent = {
    title: 'Sua campanha está pronta!',
    subtitle: 'Baixe em alta resolução, pronta para publicar.',
    marketLabel: 'Esta campanha, no mercado',
    marketPrice: 'R$ 400 a R$ 600',
    platformLabel: 'No Xamã',
    platformPrice: featuredPackageOffer?.price,
    featuredOffer: featuredPackageOffer,
    otherOptionsTitle: 'Outras opções de pacotes avulsos',
    otherOptions: packageOptions,
    onSelectOption: handleOpenPackageCheckout,
    primaryAction: {
      label: 'Comprar e baixar agora',
      onClick: handleOpenFeaturedPackageCheckout,
    },
    tertiaryLink: catalog.hasSelectablePlans
      ? {
        label: 'Posta com frequência? Assine aqui um plano →',
        onClick: () => setActiveTab('plans'),
      }
      : undefined,
    skipAction: {
      label: 'Continuar sem baixar a campanha',
      onClick: () => goToStep(DOWNLOAD_UPSELL_STEPS.DISCOUNT),
    },
  }

  const plansContent = {
    title: 'Sua campanha está pronta!',
    subtitle: 'Baixe em alta resolução, pronta para publicar.',
    marketLabel: 'Agência ou social media',
    marketPrice: 'R$ 2.000 a R$ 3.000 /mês',
    platformLabel: featuredPlanOffer.name
      ? `Plano ${featuredPlanOffer.name}`
      : 'Plano Pro',
    platformPrice: featuredPlanOffer.price,
    featuredOffer: featuredPlanOffer,
    otherOptionsTitle: 'Outras opções',
    otherOptions: planOptions,
    onSelectOption: handleOpenPlanCheckout,
    primaryAction: {
      label: featuredPlanOffer.name
        ? `Assinar o ${featuredPlanOffer.name} e baixar agora`
        : 'Assinar e baixar agora',
      onClick: handleOpenFeaturedPlanCheckout,
    },
    tertiaryLink: {
      label: 'Quer só esta campanha? Veja os pacotes avulsos →',
      onClick: () => setActiveTab('packages'),
    },
    skipAction: {
      label: 'Continuar sem baixar',
      onClick: () => goToStep(DOWNLOAD_UPSELL_STEPS.DISCOUNT),
    },
  }

  return (
    <>
      {showPreviewButton && (
        <button
          type="button"
          className={styles.upsellFlowPreviewButton}
          onClick={openFlow}
        >
          Ver modais de compra
        </button>
      )}

      <CampaignReadyModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.READY)}
        onClose={() => goToStep(DOWNLOAD_UPSELL_STEPS.DISCOUNT)}
        tabs={upsellTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        packages={packagesContent}
        plans={plansContent}
      />

      <DiscountModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.DISCOUNT)}
        onClose={() => goToStep(DOWNLOAD_UPSELL_STEPS.SINGLE)}
        offer={DISCOUNT_OFFER}
        primaryAction={{
          label: 'Usar meu desconto e baixar',
          onClick: handleOpenDiscountPackageCheckout,
        }}
        dismissAction={{
          label: 'Não quero o desconto',
          onClick: () => goToStep(DOWNLOAD_UPSELL_STEPS.SINGLE),
        }}
      />

      <SinglePurchaseModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.SINGLE)}
        onClose={() => goToStep(DOWNLOAD_UPSELL_STEPS.EXIT)}
        offer={{
          label: 'Compra única',
          price: 'R$ 19',
          description:
            'Apenas o download desta campanha em alta resolução. Não inclui artes novas.',
        }}
        primaryAction={{
          label: 'Desbloquear por R$ 19',
          onClick: handleOpenSingleDownloadCheckout,
        }}
        dismissAction={{
          label: 'Sair mesmo assim',
          onClick: () => goToStep(DOWNLOAD_UPSELL_STEPS.EXIT),
        }}
      />

      <ExitFeedbackModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.EXIT)}
        onClose={closeSuccessFlow}
        feedback={{
          options: EXIT_FEEDBACK_OPTIONS,
        }}
        onExitWithFeedback={(reason) =>
          trackUpsellExitFeedback({
            flow: UPSELL_FLOWS.CAMPAIGN_DOWNLOAD,
            campaignId,
            reasonId: reason.id,
            reasonLabel: reason.label,
          })
        }
        secondaryAction={withClose({ label: 'Voltar para a plataforma' })}
      />

      <PaymentSuccessModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.SUCCESS)}
        onClose={closeSuccessFlow}
        primaryAction={withClose(BACK_TO_CAMPAIGN_ACTION)}
        secondaryAction={withClose(CREATE_NEXT_CAMPAIGN_ACTION)}
      />

      <PaymentSuccessModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.SUCCESS_DISCOUNT)}
        onClose={closeSuccessFlow}
        description={
          <>
            Cupom {DISCOUNT_OFFER.couponCode} aplicado: você pagou{' '}
            {DISCOUNT_OFFER.price} e economizou {DISCOUNT_OFFER.savings}. Sua
            campanha já está baixando e as 10 artes estão na sua conta.
          </>
        }
        primaryAction={withClose(BACK_TO_CAMPAIGN_ACTION)}
      />

      <PaymentSuccessModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.SUCCESS_SINGLE)}
        onClose={closeSuccessFlow}
        title="Download desbloqueado!"
        description="Sua campanha está baixando em alta resolução. Quando quiser criar mais artes, você pode comprar pacotes ou assinar um plano."
        primaryAction={withClose(BACK_TO_CAMPAIGN_ACTION)}
      />

      <PaymentSuccessModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.SUCCESS_PLAN)}
        onClose={closeSuccessFlow}
        title="Assinatura ativa!"
        description={`Bem-vindo ao ${featuredPlanOffer.name ?? PLAN_FEATURED.name}: ${featuredPlanOffer.artsPerMonth ?? PLAN_FEATURED.artsPerMonth} artes por mês e download sempre liberado. Sua campanha já está baixando.`}
        primaryAction={withClose(BACK_TO_CAMPAIGN_ACTION)}
        secondaryAction={withClose(CREATE_NEXT_CAMPAIGN_ACTION)}
      />
    </>
  )
})

export default CampaignUpsellFlow
